import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, EyeOff, Eye, Trash2, ChevronDown, X, Filter } from "lucide-react";
import { AttendanceParticipants } from "@/components/office/AttendanceParticipants";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SearchBox } from "@/components/SearchBox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { serviceDisplayName } from "@/lib/services";

// Filtro multi-select riutilizzabile (chip + checkbox), coerente con gli altri filtri.
export function MultiFilter({ label, options, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="h-11 min-w-[180px] px-3 rounded-lg border border-slate-200 bg-white text-left text-sm flex items-center gap-2 hover:border-primary/40 transition-colors">
          <Filter className="h-4 w-4 text-slate-500 shrink-0" />
          {value.length === 0 ? (
            <span className="text-slate-600 truncate">{label}</span>
          ) : (
            <span className="text-slate-800 font-medium truncate">{label}: {value.length} selezionati</span>
          )}
          {value.length > 0 && (
            <span role="button" data-testid={`${testid}-clear`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange([]); }}
              className="ml-auto text-slate-500 hover:text-destructive shrink-0"><X className="h-4 w-4" /></span>
          )}
          <ChevronDown className={`h-4 w-4 text-slate-500 shrink-0 ${value.length > 0 ? "" : "ml-auto"}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-64" align="start">
        {options.length === 0 ? (
          <p className="text-sm text-slate-500 p-2">Nessun servizio disponibile.</p>
        ) : options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox checked={value.includes(o.value)} data-testid={`${testid}-opt-${o.value}`} onCheckedChange={() => toggle(o.value)} />
            <span className="text-slate-700">{o.label}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function AnagraficaAdmin({ endpoint, singular, fields, titleFn, subtitleFn, badgesFn, emptyForm, testid, extraAction, multiFilter, multiFilters, requireFilterToShow = false }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or "new"
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const filters = multiFilters || (multiFilter ? [multiFilter] : []);
  const [filterOpts, setFilterOpts] = useState({});
  const [filterVals, setFilterVals] = useState({});

  // Appalti pre-caricati a livello del padre (montato prima che si apra il form):
  // elimina la race di caricamento che bloccava la tendina all'apertura del dialog.
  const hasAppaltoField = fields.some((f) => f.type === "appalto" || f.type === "multiappalto");
  const [appaltoOpts, setAppaltoOpts] = useState([]);
  const [appaltiLoaded, setAppaltiLoaded] = useState(false);

  const fetchAppalti = useCallback(() => {
    if (!hasAppaltoField) return;
    api.get("/my-appalti")
      .then((r) => { setAppaltoOpts(r.data); setAppaltiLoaded(true); })
      .catch(() => {});
  }, [hasAppaltoField]);

  useEffect(() => { fetchAppalti(); }, [fetchAppalti]);

  // Se la richiesta resta in stallo (tipico su PWA iOS), la ritenta al ritorno
  // del focus/visibilità: così si sblocca da sola senza dover fare screenshot.
  useEffect(() => {
    if (!hasAppaltoField) return;
    const retry = () => { if (!appaltiLoaded) fetchAppalti(); };
    window.addEventListener("focus", retry);
    document.addEventListener("visibilitychange", retry);
    return () => {
      window.removeEventListener("focus", retry);
      document.removeEventListener("visibilitychange", retry);
    };
  }, [hasAppaltoField, appaltiLoaded, fetchAppalti]);

  useEffect(() => {
    filters.forEach((f, i) => {
      f.loadOptions().then((opts) => setFilterOpts((o) => ({ ...o, [i]: opts }))).catch(() => {});
    });
  }, [multiFilters, multiFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleItems = filters.reduce((acc, f, i) => {
    const sel = filterVals[i] || [];
    return sel.length > 0 ? acc.filter((it) => f.itemMatches(it, sel)) : acc;
  }, items);
  const anyFilterSelected = filters.some((_, i) => (filterVals[i] || []).length > 0);
  const gated = requireFilterToShow && !anyFilterSelected && !search.trim();

  const load = useCallback(async (q) => {
    const { data } = await api.get(`${endpoint}?search=${encodeURIComponent(q || "")}`);
    setItems(data);
    setLoading(false);
  }, [endpoint]);

  useEffect(() => { load(""); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search, load]);

  const toggleVisibility = async (item) => {
    try {
      await api.patch(`${endpoint}/${item.id}/visibility`, { hidden: !item.hidden_for_new_entries });
      toast.success(item.hidden_for_new_entries ? "Reso visibile" : "Nascosto dalle nuove registrazioni");
      load(search);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`${endpoint}/${deleting.id}`);
      toast.success(data.message || "Operazione completata");
      setDeleting(null);
      load(search);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile eliminare l'elemento");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div data-testid={`admin-${testid}`}>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {filters.length > 0 ? (
          <div data-testid={`${testid}-filterbar`}
            className="flex-1 min-w-[240px] flex flex-wrap items-center gap-2 rounded-xl border border-slate-200/80 bg-slate-50/50 p-2">
            <div className="flex-1 min-w-[180px]">
              <SearchBox value={search} onChange={setSearch} testid={`${testid}-search`} placeholder="Cerca…" />
            </div>
            {filters.map((f, i) => (
              <MultiFilter key={i} label={f.label} options={filterOpts[i] || []} value={filterVals[i] || []}
                onChange={(v) => setFilterVals((fv) => ({ ...fv, [i]: v }))}
                testid={f.testid || `${testid}-filter-${i}`} />
            ))}
          </div>
        ) : (
          <div className="flex-1 min-w-[200px]">
            <SearchBox value={search} onChange={setSearch} testid={`${testid}-search`} placeholder="Cerca…" />
          </div>
        )}
        <Button onClick={() => setEditing("new")} data-testid={`${testid}-add-btn`}
          className="rounded-lg font-semibold shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Aggiungi
        </Button>
        {extraAction}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : gated ? (
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6 text-center text-slate-500 text-sm" data-testid={`${testid}-prompt`}>
            Seleziona uno o più filtri (Appalto o Servizio), oppure digita un nome nel campo di ricerca, per visualizzare gli elementi.
          </CardContent>
        </Card>
      ) : visibleItems.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6 text-center text-slate-500 text-sm">
            {items.length === 0 ? "Nessun elemento." : "Nessun elemento corrisponde ai filtri selezionati."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid={`${testid}-list`}>
          {visibleItems.map((item, i) => (
            <motion.div key={item.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.3) }}>
              <Card className={`rounded-xl border-slate-200 shadow-sm ${item.hidden_for_new_entries ? "opacity-60" : ""}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800 truncate">{titleFn(item)}</p>
                      {badgesFn && badgesFn(item)}
                      {item.hidden_for_new_entries && (
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">Nascosto</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{subtitleFn(item)}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setEditing(item)} data-testid={`${testid}-edit-${item.id}`}
                    className="text-slate-500 shrink-0"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => toggleVisibility(item)} data-testid={`${testid}-hide-${item.id}`}
                    className="text-slate-500 shrink-0">
                    {item.hidden_for_new_entries ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleting(item)} data-testid={`${testid}-delete-${item.id}`}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {editing && (
        <EditDialog endpoint={endpoint} singular={singular} fields={fields}
          item={editing === "new" ? null : editing} emptyForm={emptyForm} testid={testid}
          appaltoOpts={appaltoOpts} appaltiLoaded={appaltiLoaded}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(search); }} />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid={`${testid}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare «{deleting ? titleFn(deleting) : ""}»?</AlertDialogTitle>
            <AlertDialogDescription>
              L'elemento verrà eliminato definitivamente <b>solo se non è mai stato utilizzato</b> in una registrazione.
              Se possiede dati storici verrà invece <b>archiviato</b> (nascosto dalle nuove registrazioni) per non compromettere registri, cronologia ed esportazioni.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-delete-cancel`}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteBusy} data-testid={`${testid}-delete-confirm`}
              className="bg-red-600 hover:bg-red-700">{deleteBusy ? "Elaborazione…" : "Elimina"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AttendancePanels({ endpoint, item, form, flushRef }) {
  const isSite = (endpoint || "").includes("operative-sites");
  const isUser = (endpoint || "").includes("school-users");
  const svcTypes = form.service_types || (item.service_type ? [item.service_type] : []);
  if (isSite) {
    // Pre/Post: elenco presenze DISTINTO per servizio anche sulla stessa sede operativa.
    const svcs = svcTypes.filter((s) => s === "pre_scuola" || s === "post_scuola");
    if (!svcs.length) return null;
    return (
      <>
        {svcs.map((svc) => (
          <AttendanceParticipants key={svc} appaltoId={item.appalto_id} service={svc}
            contextType="operational_site" contextId={item.id} flushRef={flushRef}
            title={`Utenti Presenze · ${svc === "pre_scuola" ? "Pre" : "Post"}`} />
        ))}
      </>
    );
  }
  if (isUser) {
    // Centro Estivo: gli Utenti Presenze appartengono al singolo GRUPPO (non alla sede).
    const tipologia = form.ce_tipologia || item.ce_config?.tipologia;
    if (!(svcTypes.includes("centro_estivo") && tipologia === "gruppo")) return null;
    return (
      <AttendanceParticipants appaltoId={item.appalto_id} service="centro_estivo"
        contextType="group" contextId={item.id} flushRef={flushRef} title="Utenti Presenze · Centro estivo" />
    );
  }
  return null;
}

function EditDialog({ endpoint, singular, fields, item, emptyForm, testid, appaltoOpts = [], appaltiLoaded = false, onClose, onSaved }) {
  const attFlush = useRef(new Map());
  const [form, setForm] = useState(() => {
    const base = item ? { ...emptyForm, ...item } : { ...emptyForm };
    if (item && item.service_type && !item.service_types) base.service_types = [item.service_type];
    // Centro Estivo: mappa ce_config (persistito) sui campi piatti del form in modifica.
    if (item && item.ce_config) {
      base.ce_tipologia = item.ce_config.tipologia || "";
      base.ce_site_id = item.ce_config.site_id || "";
      base.ce_monte_ore_settimanale = item.ce_config.monte_ore_settimanale ?? "";
      base.ce_monte_ore_giornaliero = item.ce_config.monte_ore_giornaliero ?? "";
    }
    return base;
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [asyncOpts, setAsyncOpts] = useState({});
  const hasAppalto = fields.some((f) => f.type === "appalto" || f.type === "multiappalto");

  // Svuota dallo stato del form i campi marcati clearOnHide quando diventano nascosti,
  // così i valori non vengono inviati né riproposti tornando a un altro servizio.
  useEffect(() => {
    setForm((prev) => {
      let upd = prev, changed = false;
      fields.forEach((fl) => {
        if (fl.clearOnHide && fl.showIf && !fl.showIf(prev)) {
          const cur = prev[fl.name];
          if (cur !== "" && cur !== null && cur !== undefined) {
            if (!changed) { upd = { ...prev }; changed = true; }
            upd[fl.name] = "";
          }
        }
      });
      return changed ? upd : prev;
    });
  }, [JSON.stringify(form.service_types), fields]);

  // Centro Estivo · Gruppo: Monte ore giornaliero precompilato a 8 (modificabile).
  useEffect(() => {
    if (form.ce_tipologia === "gruppo" && (form.ce_monte_ore_giornaliero === "" || form.ce_monte_ore_giornaliero === undefined || form.ce_monte_ore_giornaliero === null)) {
      setForm((p) => ({ ...p, ce_monte_ore_giornaliero: 8 }));
    }
  }, [form.ce_tipologia]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fields.filter((f) => f.optionsEndpoint).forEach((f) => {
      api.get(f.optionsEndpoint).then((r) => setAsyncOpts((o) => ({ ...o, [f.name]: r.data }))).catch(() => {});
    });
  }, [fields]);

  // Auto-selezione quando esiste un solo appalto (in creazione). Gli appalti arrivano
  // già pronti dal padre, quindi non c'è più attesa/blocco all'apertura del form.
  useEffect(() => {
    if (item || !appaltiLoaded || appaltoOpts.length !== 1) return;
    const singleId = appaltoOpts[0].id;
    setForm((f) => {
      const upd = { ...f };
      if (fields.some((x) => x.type === "appalto")) upd.appalto_id = singleId;
      const ma = fields.find((x) => x.type === "multiappalto");
      if (ma && (!Array.isArray(f[ma.name]) || f[ma.name].length === 0)) upd[ma.name] = [singleId];
      return upd;
    });
  }, [appaltiLoaded, appaltoOpts, item, fields]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (hasAppalto && fields.some((f) => f.type === "appalto") && !form.appalto_id) return setError("Seleziona l'appalto");
    const visible = (f) => !f.showIf || f.showIf(form);
    const missingMultiAppalto = fields.find((f) => f.type === "multiappalto" && (!Array.isArray(form[f.name]) || form[f.name].length === 0));
    if (missingMultiAppalto) return setError("Seleziona almeno un appalto");
    const missingSvc = fields.find((f) => f.type === "multiservice" && (!Array.isArray(form[f.name]) || form[f.name].length === 0));
    if (missingSvc) return setError("Seleziona almeno un servizio");
    const missing = fields.find((f) => (f.type === "select" || f.type === "asyncSelect") && f.required !== false && visible(f) && !form[f.name]);
    if (missing) return setError(`Seleziona: ${missing.label}`);
    setSaving(true);
    const optIds = appaltoOpts.map((a) => a.id);
    const payload = {};
    fields.forEach((f) => {
      if (f.type === "multiappalto") {
        // invia solo gli appalti del perimetro (checkbox visibili); il backend preserva quelli esterni
        payload[f.name] = (Array.isArray(form[f.name]) ? form[f.name] : []).filter((id) => optIds.includes(id));
        return;
      }
      if (f.type === "monteorePerService" || f.type === "rendicparams") {
        payload[f.name] = form[f.name] || {};
        return;
      }
      if (f.type === "ceMonteOre") {
        const vis = visible(f);
        payload.ce_monte_ore_settimanale = vis && form.ce_tipologia === "utente" ? (form.ce_monte_ore_settimanale ?? null) : null;
        payload.ce_monte_ore_giornaliero = vis && form.ce_tipologia === "gruppo" ? (form.ce_monte_ore_giornaliero ?? null) : null;
        return;
      }
      if (f.type === "segmented" || f.type === "ceSede") {
        payload[f.name] = visible(f) ? (form[f.name] || null) : null;
        return;
      }
      if (f.type !== "appalto" && !visible(f)) { payload[f.name] = null; return; }
      payload[f.name] = f.type === "number" ? Number(form[f.name] || 0) : f.type === "switch" ? !!form[f.name] : form[f.name];
    });
    try {
      if (item) await api.put(`${endpoint}/${item.id}`, payload);
      else await api.post(endpoint, payload);
      let added = 0;
      try {
        for (const f of attFlush.current.values()) added += await f();
      } catch (fe) { setError(fe.message); toast.error(fe.message); return; }
      toast.success(item ? `${singular} aggiornato${added ? ` · ${added} Utenti Presenze salvati` : ""}` : `${singular} creato`);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    if (f.showIf && !f.showIf(form)) return null;
    if (f.type === "multiappalto") {
      const selected = Array.isArray(form[f.name]) ? form[f.name] : [];
      const toggle = (id) => setForm({ ...form, [f.name]: selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id] });
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label || "Appalti"}</Label>
          {appaltoOpts.length === 0 ? (
            <p className="text-xs text-amber-600">Nessun appalto attivo nel tuo perimetro.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 divide-y max-h-44 overflow-y-auto" data-testid={`${testid}-appalti-list`}>
              {appaltoOpts.map((a) => (
                <label key={a.id} className="flex items-center gap-2.5 p-2.5 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={selected.includes(a.id)} data-testid={`${testid}-appalto-${a.id}`} onCheckedChange={() => toggle(a.id)} />
                  <span className="text-sm text-slate-700">{a.nome}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (f.type === "multiservice") {
      const appalto = appaltoOpts.find((a) => a.id === form.appalto_id);
      let opts = (appalto?.servizi || []).filter((s) => s.stato === "attivo");
      if (f.filterOptions) opts = opts.filter(f.filterOptions);
      const needsAppalto = !form.appalto_id;
      const selected = Array.isArray(form[f.name]) ? form[f.name] : [];
      const toggle = (slug) => setForm({ ...form, [f.name]: selected.includes(slug) ? selected.filter((x) => x !== slug) : [...selected, slug] });
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label || "Servizi"}</Label>
          {needsAppalto ? (
            <p className="text-sm text-slate-500 p-2 border border-slate-200 rounded-lg">Seleziona prima l'appalto</p>
          ) : opts.length === 0 ? (
            <p className="text-xs text-amber-600">Nessun servizio abilitato in questo appalto.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 divide-y max-h-44 overflow-y-auto" data-testid={`${testid}-services-list`}>
              {opts.map((s) => (
                <label key={s.slug} className="flex items-center gap-2.5 p-2.5 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={selected.includes(s.slug)} data-testid={`${testid}-service-${s.slug}`} onCheckedChange={() => toggle(s.slug)} />
                  <span className="text-sm text-slate-700">{serviceDisplayName(s)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (f.type === "segmented") {
      const opts = f.options || [];
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label}</Label>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50 w-full max-w-xs" data-testid={`${testid}-${f.name}`}>
            {opts.map((o) => (
              <button type="button" key={o.value}
                data-testid={`${testid}-${f.name}-${o.value}`}
                onClick={() => setForm({ ...form, [f.name]: o.value })}
                className={`flex-1 px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${form[f.name] === o.value ? "bg-primary text-white" : "text-slate-600 hover:text-slate-900"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (f.type === "ceSede") {
      const all = asyncOpts[f.name] || [];
      const opts = all.filter((s) => s.appalto_id === form.appalto_id && (s.service_types || []).includes("centro_estivo") && !s.hidden_for_new_entries);
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label || "Sede"}</Label>
          {opts.length === 0 ? (
            <p className="text-xs text-amber-600">Nessuna Sede del Centro Estivo in questo appalto. Creane una in Sedi operative.</p>
          ) : (
            <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
              <SelectTrigger data-testid={`${testid}-${f.name}`} className="h-11"><SelectValue placeholder="Seleziona la Sede…" /></SelectTrigger>
              <SelectContent>{opts.map((s) => (<SelectItem key={s.id} value={s.id}>{s.denominazione}{s.comune ? ` · ${s.comune}` : ""}</SelectItem>))}</SelectContent>
            </Select>
          )}
        </div>
      );
    }
    if (f.type === "ceMonteOre") {
      const tip = form.ce_tipologia;
      if (tip !== "utente" && tip !== "gruppo") return null;
      const isU = tip === "utente";
      const name = isU ? "ce_monte_ore_settimanale" : "ce_monte_ore_giornaliero";
      const v = form[name];
      const disp = (v === null || v === undefined || v === "") ? "" : (typeof v === "number" ? String(v).replace(".", ",") : v);
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{isU ? "Monte ore settimanale" : "Monte ore giornaliero"}</Label>
          <Input inputMode="decimal" placeholder={isU ? "es. 8 (opzionale)" : "es. 8"} value={disp}
            data-testid={`${testid}-ce-monteore`}
            onChange={(e) => setForm({ ...form, [name]: e.target.value })} className="h-11" />
          <p className="text-[11px] text-slate-400">{isU ? "Opzionale · vuoto = non configurato" : "Valore giornaliero, modificabile (default 8)"}</p>
        </div>
      );
    }
    if (f.type === "monteorePerService") {
      const selected = Array.isArray(form.service_types) ? form.service_types : [];
      const schema = f.schema || {};
      const activeSvcs = Object.keys(schema).filter((s) => selected.includes(s));
      if (activeSvcs.length === 0) return null;
      const params = form[f.name] || {};
      const setVal = (svc, value) => {
        const next = { ...(form[f.name] || {}) };
        next[svc] = value;
        setForm({ ...form, [f.name]: next });
      };
      const disp = (svc) => {
        const v = params[svc];
        if (v === null || v === undefined || v === "") return "";
        return typeof v === "number" ? String(v).replace(".", ",") : v;
      };
      return (
        <div key={f.name} className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`${testid}-monteore`}>
          <div>
            <Label className="text-sm font-semibold text-slate-700">{f.label || "Monte ore"}</Label>
            <p className="text-[11px] text-slate-400 mt-0.5">{f.helper || "Opzionale · un valore indipendente per ciascun servizio · vuoto = non configurato"}</p>
          </div>
          {activeSvcs.map((svc) => (
            <div key={svc} className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{schema[svc]?.serviceLabel || svc}</Label>
              <Input inputMode="decimal" placeholder={schema[svc]?.ph || "es. 8"} value={disp(svc)}
                data-testid={`${testid}-monteore-${svc}`}
                onChange={(e) => setVal(svc, e.target.value)} className="h-10" />
            </div>
          ))}
        </div>
      );
    }
    if (f.type === "rendicparams") {
      const selected = Array.isArray(form.service_types) ? form.service_types : [];
      const schema = f.schema || {};
      const activeSvcs = Object.keys(schema).filter((s) => selected.includes(s));
      if (activeSvcs.length === 0) return null;
      const params = form[f.name] || {};
      const setParam = (svc, key, value) => {
        const next = { ...(form[f.name] || {}) };
        next[svc] = { ...(next[svc] || {}), [key]: value };
        setForm({ ...form, [f.name]: next });
      };
      const disp = (svc, key) => {
        const v = params[svc]?.[key];
        if (v === null || v === undefined || v === "") return "";
        return typeof v === "number" ? String(v).replace(".", ",") : v;
      };
      const label = f.labelFn || ((s) => s);
      return (
        <div key={f.name} className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`${testid}-rendicparams`}>
          <div>
            <Label className="text-sm font-semibold text-slate-700">Parametri di rendicontazione</Label>
            <p className="text-[11px] text-slate-400 mt-0.5">{f.helper || "Opzionale · utilizzato per la futura rendicontazione"}</p>
          </div>
          {activeSvcs.map((svc) => {
            const subFields = schema[svc] || [];
            return (
              <div key={svc} className="space-y-1.5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label(svc)}</p>
                <div className={`grid gap-2 ${subFields.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {subFields.map((sf) => (
                    <div key={sf.key} className="space-y-1">
                      <Label className="text-xs text-slate-600">{sf.label}</Label>
                      <Input inputMode="decimal" placeholder={sf.ph} value={disp(svc, sf.key)}
                        data-testid={`${testid}-rendic-${svc}-${sf.key}`}
                        onChange={(e) => setParam(svc, sf.key, e.target.value)} className="h-10" />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    if (f.type === "appalto") {
      const single = appaltoOpts.length === 1;
      const loading = hasAppalto && !appaltiLoaded;
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label || "Appalto"}</Label>
          <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v, ...(f.resets || []).reduce((a, k) => ({ ...a, [k]: "" }), {}) })} disabled={single || loading}>
            <SelectTrigger data-testid={`${testid}-field-appalto_id`} className="h-11"><SelectValue placeholder={loading ? "Caricamento appalti…" : "Seleziona l'appalto…"} /></SelectTrigger>
            <SelectContent>{appaltoOpts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>))}</SelectContent>
          </Select>
          {appaltiLoaded && appaltoOpts.length === 0 && <p className="text-xs text-amber-600">Nessun appalto attivo nel tuo perimetro.</p>}
        </div>
      );
    }
    if (f.type === "switch") {
      return (
        <div key={f.name} className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
          <Label htmlFor={f.name} className="cursor-pointer">{f.label}</Label>
          <Switch id={f.name} checked={!!form[f.name]} data-testid={`${testid}-field-${f.name}`}
            onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} />
        </div>
      );
    }
    if (f.type === "select") {
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label}</Label>
          <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
            <SelectTrigger data-testid={`${testid}-field-${f.name}`} className="h-11"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>{f.options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      );
    }
    if (f.type === "asyncSelect") {
      let list = asyncOpts[f.name] || [];
      if (f.filterByAppalto) list = list.filter((x) => Array.isArray(x.appalto_ids) ? x.appalto_ids.includes(form.appalto_id) : x.appalto_id === form.appalto_id);
      const selected = list.find((x) => x.id === form[f.name]);
      const needsAppalto = f.filterByAppalto && !form.appalto_id;
      return (
        <div key={f.name} className="space-y-1.5">
          <Label>{f.label}</Label>
          <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })} disabled={needsAppalto}>
            <SelectTrigger data-testid={`${testid}-field-${f.name}`} className="h-11"><SelectValue placeholder={needsAppalto ? "Seleziona prima l'appalto" : "Seleziona…"} /></SelectTrigger>
            <SelectContent>{list.map((x) => (<SelectItem key={x.id} value={x.id}>{f.optionLabel(x)}</SelectItem>))}</SelectContent>
          </Select>
          {selected && f.infoFn && (
            <p className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid={`${testid}-field-${f.name}-info`}>
              {f.infoFn(selected)}
            </p>
          )}
        </div>
      );
    }
    const isReq = typeof f.required === "function" ? f.required(form) : (f.required !== false);
    return (
      <div key={f.name} className="space-y-1.5">
        <Label htmlFor={f.name}>{f.label}</Label>
        <Input id={f.name} type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"} required={isReq}
          value={form[f.name] ?? ""} data-testid={`${testid}-field-${f.name}`}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="h-11" />
        {f.helper && <p className="text-[11px] text-slate-400 mt-0.5">{f.helper}</p>}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? `Modifica ${singular}` : `Nuovo ${singular}`}</DialogTitle>
          <DialogDescription>Compila i campi e salva.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && (
            <div data-testid={`${testid}-form-error`} className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>
          )}
          {fields.map(renderField)}
          {item && item.id && <AttendancePanels endpoint={endpoint} item={item} form={form} flushRef={attFlush} />}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid={`${testid}-submit`} className="w-full rounded-lg h-11 font-semibold">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
